import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { ShieldOff } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle, Page, PageContent, Skeleton } from '@bliks-io/ui';
import { adminNavigation, visibleAdminNavigation } from './layout/adminNavigation';
import { usePlatformRoles } from '@/hooks/usePlatformRoles';

/**
 * Wo jemand landet, der die Plattform betritt.
 *
 * Bis zum 21.09. war das fest `/admin/tenants` — was richtig war, solange
 * jeder alles durfte. Seit es Rollen gibt (ADR 0042), ist es die erste Seite,
 * die **diese** Person sehen darf: wer nur liest, käme sonst auf den
 * Arbeitsbereichen an, die nicht in seinem Menü stehen.
 *
 * Und wer gar nichts darf, bekommt das gesagt. Ein leerer Bildschirm ohne
 * Menü liest sich als kaputte Software; er ist aber ein Konto, dem jemand
 * vergessen hat, eine Rolle zu geben.
 */
export function AdminLandingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can, isLoading } = usePlatformRoles();
  const first = visibleAdminNavigation(adminNavigation, can)[0];

  useEffect(
    function goToTheFirstPageThisPersonMaySee() {
      if (isLoading || !first?.url) return;
      void navigate({ to: `/admin${first.url}`, replace: true });
    },
    [isLoading, first?.url, navigate]
  );

  if (isLoading || first) {
    return (
      <Page>
        <PageContent>
          <Skeleton className="h-32 w-full" />
        </PageContent>
      </Page>
    );
  }

  return (
    <Page>
      <PageContent>
        <Alert>
          <ShieldOff />
          <AlertTitle>{t('adminSettings.superAdmin.noAccessTitle')}</AlertTitle>
          <AlertDescription>{t('adminSettings.superAdmin.noAccessBody')}</AlertDescription>
        </Alert>
      </PageContent>
    </Page>
  );
}
